import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, withModifiers as _withModifiers, Fragment as _Fragment, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "flex flex-1 items-center gap-2" }
const _hoisted_2 = { class: "grow text-lg font-medium" }
const _hoisted_3 = { class: "flex items-center gap-2" }
const _hoisted_4 = { class: "text-gray-60 text-sm font-medium" }
const _hoisted_5 = {
  key: 0,
  class: "text-center text-gray-50"
}
const _hoisted_6 = {
  key: 0,
  class: "flex items-start gap-6"
}
const _hoisted_7 = { class: "text-gray-60 mr-auto flex flex-col text-sm" }
const _hoisted_8 = { class: "flex flex-row gap-1" }
const _hoisted_9 = {
  key: 1,
  class: "text-gray-80 ml-1 font-medium"
}
const _hoisted_10 = {
  key: 2,
  class: "text-red-50"
}
const _hoisted_11 = { class: "ml-1" }
const _hoisted_12 = { class: "bg-gray-10 ml-1 flex h-5 w-5 self-center rounded-xl p-1" }
const _hoisted_13 = { class: "ml-1" }
const _hoisted_14 = { class: "bg-gray-10 ml-1 flex h-5 w-5 self-center rounded-xl p-1" }
const _hoisted_15 = ["href"]

import { useRouter } from 'vue-router'
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { InfoIcon, LockIcon, UnlockIcon } from '@klausapp/ui-kit/icons'
import { UiButton, UiFormField, UiInput, UiSpinner, UiAlert } from '@klausapp/ui-kit'
import { v4 as uuid } from 'uuid'

import { formatDateIntl } from '@/utils/date'
import QModal from '@/components/QModal.vue'
import { toast } from '@/components/toast'
import analytics, { track } from '@/utils/analytics'
import { session } from '@/composables/useSession'
import i18n from '@/i18n'
import { hasWorkspaceRole } from '@/utils/roleUtils'
import { bus } from '@/utils/bus'
import { delay } from '@/components/Tippy'
import useAutoQaDateWarning from '@/composables/useAutoQaDateWarning'
import useReviewSidebar from '@/composables/useReviewSidebar/useReviewSidebar'
import QToggle from '@/components/QToggle.vue'
import useAiConversations from '@/modules/settings/composables/useAiConversations'
import PromptTestingWorkspaceSelection from '@/modules/settings/components/ai/PromptTestingWorkspaceSelection.vue'
import usePromptTestingWorkspace from '@/modules/settings/composables/usePromptTestingWorkspace'
import { FilterModalType } from '@/components/filters/types'
import { workspacesLoading } from '@/composables/useWorkspaceList'
import useFilterValues from '@/modules/conversations/composables/useFilterValues'

import {
  activeFilterId,
  privateFilters,
  publicFilters,
  setActiveFilterId,
} from '../../composables/useConversationFilters'

import {
  constructFilterOptions,
  constructDynamicFilterOptions,
  dateConditions,
  deconstructFilterOptions,
  generateFilterLabels,
  getDayjsRangeFromOption,
  type DeconstructedOption,
} from '../../utils/filters'
import {
  type BaseFilter,
  createFilter,
  editFilter,
  getFilterOptions as getFilterOptionsApi,
  getFilterDetails,
  memGetFilterTicketCount,
} from '../../api/filters'
import FilterOptions from './FilterOptions.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'FilterModal',
  props: {
    showModal: { type: Boolean },
    showNoFiltersModal: { type: Boolean },
    public: { type: Boolean },
    promptNewFilter: { type: Boolean },
    editableFilter: {},
    promptTesting: { type: Boolean },
    canConvertToAssignment: { type: Boolean },
    initialFilterOptions: {}
  },
  emits: ["close", "update", "stop-ticket-list-loading", "select-tickets-for-testing"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { displayAsEmoji } = useReviewSidebar()
const { promptTestingConversationsFilterOptions } = useAiConversations()
const { activeWorkspace, revertWorkspace, submitWorkspace } = usePromptTestingWorkspace()
const { fetchedFilterOptions } = useFilterValues()

const router = useRouter()
const editModal = ref(false)
const editFilterId = ref(null)
const newFilterPublicity = ref(false)
const newFilterOptions = ref<DeconstructedOption[]>([])
const newFilterName = ref('')
const saveClicked = ref(false)
const filterTicketCount = ref<number>(0)
const showTicketCountError = ref(false)
const loadingModal = ref(false)
const loadingTickets = ref(false)
const loadingFilterOptions = ref(false)
const filterUpdatedInfo = ref('')
const nameFieldRef = ref(null)
const disableSelectingTickets = ref(false)

const noFilters = computed(() => !publicFilters.value.length && !privateFilters.value.length)
const hasTimeframeFilter = computed(() => {
  return newFilterOptions.value.some(({ option }) => dateConditions.includes(option.name))
})

/* conversation filters should always have time constraint so requests wouldn't take too long */
const getEnhancedFilterOptions = (options: DeconstructedOption[]) => {
  const enhancedOptions = constructFilterOptions(
    options.filter(
      (o) => !o.option.isAutoQa && !o.option.isSpotlight && !o.option.isCustomCategory && !o.option.isIntelligent,
    ),
  )

  if (!hasTimeframeFilter.value) {
    enhancedOptions.push({ type: 'DEFAULT', option: 'TICKET_CREATED_DATE', condition: 'DATE_30_DAYS' })
  }

  return enhancedOptions
}

watch(
  () => props.showModal,
  (showModal) => {
    if (showModal) showFilterModal()
    else saveClicked.value = false
  },
)

const selectedAutoQaFilterNames = computed(() =>
  newFilterOptions.value.filter((o) => o.option.isAutoQa).map(({ option }) => option.name?.toLowerCase()),
)

const dateRange = computed(() => {
  const createdDateOption = newFilterOptions.value.find(({ option }) => option.name === 'TICKET_CREATED_DATE')
  if (!createdDateOption) return null

  return getDayjsRangeFromOption(createdDateOption)
})

const { warningMessage } = useAutoQaDateWarning({
  workspaceId: session.workspace.id,
  dateRange,
  usedAutoQaCategories: selectedAutoQaFilterNames,
})

const disableSubmit = computed(() => {
  if (props.promptTesting)
    return (
      loadingTickets.value ||
      loadingFilterOptions.value ||
      disableSelectingTickets.value ||
      filterTicketCount.value === 0
    )
  return false
})

const cancelModal = () => {
  emit('close')
  if (props.promptTesting) {
    newFilterOptions.value = []
    revertWorkspace()
  }
}

watch(
  [newFilterOptions, workspacesLoading],
  async ([options]) => {
    if (!options.length) return
    loadingTickets.value = true

    try {
      showTicketCountError.value = false
      const { data } = await memGetFilterTicketCount(
        {
          isPublic: newFilterPublicity.value,
          name: newFilterName.value || 'temporary-name',
          options: getEnhancedFilterOptions(options),
          autoQaOptions: constructFilterOptions(options.filter((o) => o.option.isAutoQa)),
          spotlightOptions: constructFilterOptions(options.filter((o) => o.option.isSpotlight)),
          customCategoryOptions: constructFilterOptions(options.filter((o) => o.option.isCustomCategory)),
          intelligentFilterOptions: constructFilterOptions(options.filter((o) => o.option.isIntelligent)),
        },
        props.promptTesting && activeWorkspace.value?.id
          ? `payments/${session.account.id}/teams/${activeWorkspace.value.id}`
          : undefined,
      )
      filterTicketCount.value = data
    } catch {
      filterTicketCount.value = 0
      showTicketCountError.value = true
    } finally {
      loadingTickets.value = false
    }
  },
  { deep: true },
)

onMounted(() => {
  if (props.promptNewFilter) showFilterModal()
})

onBeforeUnmount(() => {
  editModal.value = false
})

const addFilter = async () => {
  if (props.promptTesting) {
    promptTestingConversationsFilterOptions.value = newFilterOptions.value
    selectTicketsForPromptTesting()
    submitWorkspace()
    return
  }

  saveClicked.value = true

  if (!newFilterName.value?.length) {
    toast({ status: 'error', message: i18n.t('filter.empty_name_toast') })
    nameFieldRef.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  if (!hasTimeframeFilter.value) {
    toast({ status: 'error', message: i18n.t('filter.date_condition_toast') })
    document.querySelector('[data-testid=date-condition-group]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }

  const data = {
    isPublic: newFilterPublicity.value,
    name: newFilterName.value,
    options: constructFilterOptions(
      newFilterOptions.value.filter(
        (o) => !o.option.isAutoQa && !o.option.isSpotlight && !o.option.isCustomCategory && !o.option.isIntelligent,
      ),
    ),
    autoQaOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isAutoQa)),
    spotlightOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isSpotlight)),
    customCategoryOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isCustomCategory)),
    intelligentFilterOptions: constructFilterOptions(newFilterOptions.value.filter((o) => o.option.isIntelligent)),
  }

  let filterId: number | undefined = undefined

  if (editModal.value) {
    if (editFilterId.value) {
      filterId = await editFilter({ id: editFilterId.value, data }).then(({ data }) => data)
      emit('update')
    }
  } else {
    filterId = await createFilter(data).then(({ data }) => data)
  }

  if (filterId) setFilter(filterId)
  analytics.filterChanged({ editModal: editModal.value, isPublic: newFilterPublicity.value, name: newFilterName.value })

  const hasBotFilter = newFilterOptions.value.some((o) => ['BOTS', 'AI_AGENTS'].includes(o.option.name))
  const hasBotReplyFilter = newFilterOptions.value.some((o) =>
    ['BOT_REPLY_COUNT', 'AI_AGENT_REPLY_COUNT'].includes(o.option.name),
  )
  if (hasBotFilter) analytics.botFilterExists()
  if (hasBotReplyFilter) analytics.botReplyFilterExists()

  data.autoQaOptions.forEach((o) => track(`Conversation filter saved with condition: ${o.option.toLowerCase()}`))

  toast({
    status: 'success',
    message: editModal.value
      ? i18n.t('conversations.filters.update_success_toast')
      : i18n.t('conversations.filters.add_success_toast'),
  })
  editModal.value = false
  emit('close')
  newFilterOptions.value = []
  newFilterName.value = ''
  newFilterPublicity.value = false
}

const selectTicketsForPromptTesting = () => {
  emit('select-tickets-for-testing', filterTicketCount.value)
  newFilterOptions.value = []
}

const showFilterModal = async (_?: any, localFilterOptions?: any) => {
  editModal.value = false
  loadingModal.value = true

  try {
    if (!fetchedFilterOptions.value) await getFilterOptions()
  } catch {
    toast({ status: 'error', message: i18n.t('conversations.filters.form.loading_error') })
    loadingModal.value = false
    return
  }

  if (props.editableFilter) {
    editModal.value = true
    // Load filter details
    editFilterId.value = props.editableFilter.filterId
    newFilterName.value = props.editableFilter.name
    newFilterPublicity.value = props.editableFilter.isPublic
    filterUpdatedInfo.value = getFilterUpdatedInfo(props.editableFilter)

    const filterDetails = await getFilterDetails(props.editableFilter.filterId, false).then(({ data }) => data)

    newFilterOptions.value = deconstructFilterOptions({
      options: filterDetails.options || [],
      autoQaOptions: filterDetails.autoQaOptions || [],
      spotlightOptions: filterDetails.spotlightOptions || [],
      customCategoryOptions: filterDetails.customCategoryOptions || [],
      intelligentFilterOptions: filterDetails.intelligentFilterOptions || [],
      defaultOptions: fetchedFilterOptions.value?.defaultFilterOptions,
      defaultAutoQaOptions: fetchedFilterOptions.value?.autoQaFilterOptions,
      defaultSpotlightOptions: fetchedFilterOptions.value?.spotlightFilterOptions,
      defaultCustomCategoryOptions: fetchedFilterOptions.value?.customCategoryFilterOptions,
      defaultIntelligentFilterOptions: fetchedFilterOptions.value?.intelligentFilterOptions,
      displayAsEmoji: displayAsEmoji.value,
    })
  } else {
    editFilterId.value = null
    newFilterName.value = ''
    if (props.initialFilterOptions) {
      newFilterOptions.value = props.initialFilterOptions
    } else {
      newFilterOptions.value = localFilterOptions || [
        {
          id: uuid(),
          option: { name: 'TICKET_CREATED_DATE', label: 'filter.option.label.ticket_created_date' },
          condition: { name: 'DATE_30_DAYS', label: 'filter.condition.label.date_30_days', valueRequired: false },
          values: [],
        },
      ]
    }

    newFilterPublicity.value = props.public
    filterUpdatedInfo.value = undefined
  }

  loadingModal.value = false
  filterTicketCount.value = 0
}

async function switchWorkspace(workspaceId: number) {
  loadingModal.value = true
  try {
    await getFilterOptions(`payments/${session.account.id}/teams/${workspaceId}`)
    const availableOptionNames = [...Object.values(fetchedFilterOptions.value || {}).flat()].map((o) => o.filterOption)
    newFilterOptions.value = newFilterOptions.value.filter((o) => availableOptionNames.includes(o.option.name))
  } catch {
    toast({ status: 'error', message: i18n.t('conversations.filters.form.loading_error') })
    return
  } finally {
    loadingModal.value = false
  }
}

const displayNoFiltersPrompt = async () => {
  const assignedToAgentFilter = constructDynamicFilterOptions({
    forFilter: true,
    options: ['ASSIGNEE'],
    conditions: ['LONG_LIST_EQUALS'],
    values: [1],
  })
  loadingModal.value = true

  if (!fetchedFilterOptions.value) {
    loadingFilterOptions.value = true
    await getFilterOptionsApi()
    loadingFilterOptions.value = false
  }
  const labelledOptions = fetchedFilterOptions.value?.defaultFilterOptions.length
    ? generateFilterLabels(assignedToAgentFilter, fetchedFilterOptions.value?.defaultFilterOptions)
    : []
  await showFilterModal(null, labelledOptions)
  newFilterName.value = 'My tickets'
  bus.$emit('ticket-hide-loader')
}

const getFilterUpdatedInfo = (filter) => {
  const { updated, created, updateByName, ownerName } = filter
  const agentName = updateByName || ownerName || i18n.t('universal.former_member', 2)
  return updated
    ? i18n.t('conversations.filters.form.filter_updated', { time: formatDateIntl(updated || created), agentName })
    : i18n.t('conversations.filters.form.filter_created', { time: formatDateIntl(updated || created), agentName })
}

const getFilterOptions = async (reqPrefix?: string) => {
  fetchedFilterOptions.value = await getFilterOptionsApi(reqPrefix)
    .then(({ data }) => data)
    .catch((err) => {
      throw err
    })
}

const setFilter = (id: number | string) => {
  const isNumericFilter = !isNaN(id as number)
  setActiveFilterId(id)
  if (!activeFilterId.value || (isNumericFilter && activeFilterId.value === id)) emit('update')
}

watch(
  () => props.showNoFiltersModal,
  (state) => {
    if (state) {
      displayNoFiltersPrompt()
      emit('stop-ticket-list-loading')
    }
  },
  { immediate: true },
)

const convertToAssignment = () => {
  editModal.value = false
  emit('close')
  router.push({
    name: 'workspace.assignments.agent-qa.new',
    params: { id: session.workspace.id },
    query: { name: newFilterName.value || '', conditions: encodeURIComponent(JSON.stringify(newFilterOptions.value)) },
  })
}

return (_ctx: any,_cache: any) => {
  const _component_i18n_t = _resolveComponent("i18n-t")!
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createBlock(QModal, {
    show: _ctx.showModal || _ctx.showNoFiltersModal,
    title: 
      editModal.value ? _ctx.$t('conversations.filters.form.update_heading') : _ctx.$t('conversations.filters.form.create_heading')
    ,
    "hanging-content": filterUpdatedInfo.value,
    scrollable: "",
    wide: "",
    onClose: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('close')))
  }, _createSlots({
    default: _withCtx(() => [
      (loadingModal.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("p", null, _toDisplayString(_ctx.$t('conversations.filters.form.loading_options_message')), 1 /* TEXT */),
            _createVNode(_unref(UiSpinner), { class: "modal-loader" })
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (noFilters.value && !_ctx.promptTesting)
              ? (_openBlock(), _createBlock(_unref(UiAlert), {
                  key: 0,
                  warning: "",
                  class: "mb-6"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_i18n_t, { keypath: "conversations.filters.form.no_filters_message" }, {
                      recommendedFilter: _withCtx(() => [
                        _cache[5] || (_cache[5] = _createTextVNode(" \"", -1 /* CACHED */)),
                        _createElementVNode("strong", null, _toDisplayString(_ctx.$t('conversations.filters.form.recommended_filter')), 1 /* TEXT */),
                        _cache[6] || (_cache[6] = _createTextVNode("\" ", -1 /* CACHED */))
                      ]),
                      _: 1 /* STABLE */
                    })
                  ]),
                  _: 1 /* STABLE */
                }))
              : _createCommentVNode("v-if", true),
            (_ctx.promptTesting && filterTicketCount.value === 0 && !loadingTickets.value)
              ? (_openBlock(), _createBlock(_unref(UiAlert), {
                  key: 1,
                  error: "",
                  small: "",
                  class: "mb-6"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('settings.ai.conversations_filter_modal.error')), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }))
              : _createCommentVNode("v-if", true),
            _createElementVNode("form", {
              class: "settings-form !mt-5",
              onSubmit: _withModifiers(addFilter, ["prevent"])
            }, [
              (!_ctx.promptTesting)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _createVNode(_unref(UiFormField), {
                      ref_key: "nameFieldRef",
                      ref: nameFieldRef,
                      class: "w-80",
                      label: _ctx.$t('conversations.filters.form.name_field_label'),
                      error: !newFilterName.value && saveClicked.value ? _ctx.$t('filter.name_empty') : undefined
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(UiInput), {
                          modelValue: newFilterName.value,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newFilterName).value = $event)),
                          modelModifiers: { trim: true },
                          name: "name",
                          required: "",
                          maxlength: "255"
                        }, null, 8 /* PROPS */, ["modelValue"])
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["label", "error"]),
                    (!_unref(hasWorkspaceRole)('AGENT'))
                      ? (_openBlock(), _createBlock(QToggle, {
                          key: 0,
                          modelValue: newFilterPublicity.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newFilterPublicity).value = $event)),
                          label: _ctx.$t('conversations.filters.form.visibility_toggle_label'),
                          options: [
              { label: _ctx.$t('conversations.filters.form.public_option_label'), value: true, icon: _unref(UnlockIcon) },
              { label: _ctx.$t('conversations.filters.form.private_option_label'), value: false, icon: _unref(LockIcon) },
            ]
                        }, null, 8 /* PROPS */, ["modelValue", "label", "options"]))
                      : _createCommentVNode("v-if", true)
                  ]))
                : _createCommentVNode("v-if", true),
              (_unref(warningMessage))
                ? (_openBlock(), _createBlock(_unref(UiAlert), {
                    key: 1,
                    warning: "",
                    class: "my-6"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(warningMessage)), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true),
              (_unref(fetchedFilterOptions))
                ? (_openBlock(), _createBlock(FilterOptions, {
                    key: 2,
                    id: "add-new-filters",
                    modelValue: newFilterOptions.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newFilterOptions).value = $event)),
                    class: "mb-6 mt-14",
                    "edit-modal": editModal.value,
                    "is-modal": "",
                    options: _unref(fetchedFilterOptions).defaultFilterOptions,
                    "autoqa-fields": _unref(fetchedFilterOptions).autoQaFilterOptions,
                    "spotlight-fields": _unref(fetchedFilterOptions).spotlightFilterOptions,
                    "custom-category-fields": _unref(fetchedFilterOptions).customCategoryFilterOptions,
                    "intelligent-filter-fields": _unref(fetchedFilterOptions).intelligentFilterOptions,
                    "hide-all-conversation-filters": _ctx.promptTesting,
                    "modal-type": _ctx.promptTesting && _unref(FilterModalType).PromptTesting,
                    onDisableSelectingTickets: _cache[3] || (_cache[3] = ($event: any) => (disableSelectingTickets.value = !$event))
                  }, null, 8 /* PROPS */, ["modelValue", "edit-modal", "options", "autoqa-fields", "spotlight-fields", "custom-category-fields", "intelligent-filter-fields", "hide-all-conversation-filters", "modal-type"]))
                : _createCommentVNode("v-if", true)
            ], 32 /* NEED_HYDRATION */)
          ], 64 /* STABLE_FRAGMENT */))
    ]),
    _: 2 /* DYNAMIC */
  }, [
    (_ctx.promptTesting)
      ? {
          name: "header",
          fn: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t('settings.ai.conversations_filter_modal.title')), 1 /* TEXT */),
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.$t('settings.ai.conversations_filter_modal.workspace_label')), 1 /* TEXT */),
                _createVNode(PromptTestingWorkspaceSelection, { onSwitchWorkspace: switchWorkspace })
              ])
            ])
          ]),
          key: "0"
        }
      : undefined,
    (!loadingModal.value)
      ? {
          name: "actions",
          fn: _withCtx(() => [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("span", _hoisted_8, [
                _createVNode(_component_i18n_t, {
                  tag: "div",
                  keypath: "conversations.filters.form.ticket_count_label_with_appendix",
                  class: "flex",
                  plural: filterTicketCount.value
                }, {
                  count: _withCtx(() => [
                    (loadingTickets.value)
                      ? (_openBlock(), _createBlock(_unref(UiSpinner), {
                          key: 0,
                          class: "tickets-loader small"
                        }))
                      : (_openBlock(), _createElementBlock("span", _hoisted_9, [
                          (showTicketCountError.value)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createTextVNode(_toDisplayString(_ctx.$t('conversations.filters.form.ticket_count_na')), 1 /* TEXT */)
                              ], 64 /* STABLE_FRAGMENT */))
                            : (filterTicketCount.value > 999 && !_unref(session).features.showExactTicketCount)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createTextVNode(_toDisplayString(_ctx.$n(999, { signDisplay: 'always' })), 1 /* TEXT */)
                                ], 64 /* STABLE_FRAGMENT */))
                              : (filterTicketCount.value === 0 && _ctx.promptTesting && !loadingTickets.value)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(filterTicketCount.value), 1 /* TEXT */))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                    _createTextVNode(_toDisplayString(filterTicketCount.value), 1 /* TEXT */)
                                  ], 64 /* STABLE_FRAGMENT */))
                        ]))
                  ]),
                  appendix: _withCtx(() => [
                    (!loadingTickets.value && !hasTimeframeFilter.value && !_ctx.promptTesting)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createElementVNode("span", _hoisted_11, _toDisplayString(_ctx.$t('conversations.filters.form.over_last_30_days')), 1 /* TEXT */),
                          _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_12, [
                            _createVNode(_unref(InfoIcon), { class: "icon-m self-center" })
                          ])), [
                            [_directive_tippy, { content: _ctx.$t('conversations.filters.form.timeframe_tooltip'), delay: _unref(delay) }]
                          ])
                        ], 64 /* STABLE_FRAGMENT */))
                      : _createCommentVNode("v-if", true),
                    (_ctx.promptTesting && filterTicketCount.value >= 100)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createElementVNode("span", _hoisted_13, _toDisplayString(_ctx.$t('settings.ai.conversations_filter_modal.count_description', { count: 100 })), 1 /* TEXT */),
                          _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_14, [
                            _createVNode(_unref(InfoIcon), { class: "icon-m self-center" })
                          ])), [
                            [_directive_tippy, {
                    content: _ctx.$t('settings.ai.conversations_filter_modal.count_info_tooltip', { count: 100 }),
                    delay: _unref(delay),
                  }]
                          ])
                        ], 64 /* STABLE_FRAGMENT */))
                      : _createCommentVNode("v-if", true)
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["plural"])
              ]),
              _createElementVNode("a", {
                href: _ctx.$t('support_links.find_conversations_to_review'),
                target: "_blank",
                rel: "noopener noreferrer",
                class: "content-link"
              }, _toDisplayString(_ctx.$t('conversations.filters.form.read_more')), 9 /* TEXT, PROPS */, _hoisted_15)
            ]),
            (_ctx.canConvertToAssignment)
              ? (_openBlock(), _createBlock(_unref(UiButton), {
                  key: 0,
                  class: "mr-3",
                  secondary: "",
                  onClick: convertToAssignment
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('conversations.filters.form.convert')), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }))
              : _createCommentVNode("v-if", true),
            (!noFilters.value || _ctx.promptTesting)
              ? (_openBlock(), _createBlock(_unref(UiButton), {
                  key: 1,
                  secondary: "",
                  class: "mr-3",
                  onClick: cancelModal
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('universal.cancel')), 1 /* TEXT */)
                  ]),
                  _: 1 /* STABLE */
                }))
              : _createCommentVNode("v-if", true),
            _createVNode(_unref(UiButton), {
              type: "submit",
              disabled: disableSubmit.value,
              onClick: addFilter
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(editModal.value
            ? _ctx.$t('conversations.filters.form.update_submit')
            : _ctx.promptTesting
              ? _ctx.$t('settings.ai.conversations_filter_modal.submit_button')
              : _ctx.$t('conversations.filters.form.create_submit')), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["disabled"])
          ]),
          key: "1"
        }
      : undefined
  ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["show", "title", "hanging-content"]))
}
}

})